import { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator, TrendingUp, DollarSign, Percent, Home, Wrench } from "lucide-react";

const currency = (n) => (isFinite(n) ? `$${Math.round(n).toLocaleString()}` : "—");
const pct = (n) => (isFinite(n) ? `${n.toFixed(1)}%` : "—");

function Field({ label, value, onChange, suffix, testid }) {
  return (
    <div>
      <Label className="mb-2 block text-xs uppercase tracking-widest text-neutral-500">{label}</Label>
      <div className="relative">
        <Input
          data-testid={testid}
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          className="rounded-full h-12 pl-5 pr-14 border-neutral-200"
        />
        {suffix && <span className="absolute right-5 top-1/2 -translate-y-1/2 text-sm text-neutral-400">{suffix}</span>}
      </div>
    </div>
  );
}

function Result({ label, value, big }) {
  return (
    <div className={`rounded-2xl bg-white border border-neutral-100 p-5 ${big ? "col-span-2" : ""}`}>
      <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">{label}</div>
      <div className={`font-display ${big ? "text-4xl" : "text-2xl"} text-brand-pink`}>{value}</div>
    </div>
  );
}

function Card({ title, icon: Icon, children, testid }) {
  return (
    <div data-testid={testid} className="rounded-3xl bg-white border border-neutral-100 p-8 card-soft">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-2xl bg-brand-blush flex items-center justify-center">
          <Icon className="w-5 h-5 text-brand-pink" />
        </div>
        <div className="font-display text-2xl">{title}</div>
      </div>
      {children}
    </div>
  );
}

export default function Calculators() {
  // ROI + Cash Flow + Occupancy shared inputs
  const [purchase, setPurchase] = useState(350000);
  const [down, setDown] = useState(70000);
  const [nightly, setNightly] = useState(220);
  const [occupancy, setOccupancy] = useState(65);
  const [expenses, setExpenses] = useState(1800);
  const [nightsBooked, setNightsBooked] = useState(220);
  const [revenue, setRevenue] = useState(58000);
  const [totalNights, setTotalNights] = useState(365);
  const [availableNights, setAvailableNights] = useState(300);

  // Break-even
  const [monthlyFixed, setMonthlyFixed] = useState(2400);
  const [avgAdr, setAvgAdr] = useState(220);
  const [varCostPerNight, setVarCostPerNight] = useState(35);

  // Mortgage
  const [loan, setLoan] = useState(280000);
  const [rate, setRate] = useState(7.25);
  const [years, setYears] = useState(30);

  // Cleaning cost
  const [cleaningFee, setCleaningFee] = useState(120);
  const [cleaningCost, setCleaningCost] = useState(75);
  const [turnovers, setTurnovers] = useState(20);

  // Co-host commission
  const [grossRev, setGrossRev] = useState(9500);
  const [commissionPct, setCommissionPct] = useState(20);

  // Calculations
  const annualRevenue = (nightly || 0) * ((occupancy || 0) / 100) * 365;
  const annualExpenses = (expenses || 0) * 12;
  const noi = annualRevenue - annualExpenses;
  const cashOnCash = down ? (noi / down) * 100 : 0;

  const cashFlowMonthly = annualRevenue / 12 - (expenses || 0);

  const adr = nightsBooked ? (revenue || 0) / nightsBooked : 0;
  const revpar = totalNights ? (revenue || 0) / totalNights : 0;
  const occRate = availableNights ? ((nightsBooked || 0) / availableNights) * 100 : 0;

  const contrib = (avgAdr || 0) - (varCostPerNight || 0);
  const breakEvenNights = contrib > 0 ? (monthlyFixed || 0) / contrib : Infinity;

  // Mortgage (M = P r (1+r)^n / ((1+r)^n - 1))
  const monthlyRate = (rate || 0) / 100 / 12;
  const n = (years || 0) * 12;
  const monthlyPayment = monthlyRate === 0 ? (loan || 0) / n : (loan || 0) * monthlyRate * Math.pow(1 + monthlyRate, n) / (Math.pow(1 + monthlyRate, n) - 1);

  const cleaningMargin = ((cleaningFee || 0) - (cleaningCost || 0)) * (turnovers || 0);
  const cleaningPct = cleaningFee ? (((cleaningFee || 0) - (cleaningCost || 0)) / cleaningFee) * 100 : 0;

  const commission = ((grossRev || 0) * (commissionPct || 0)) / 100;
  const ownerNet = (grossRev || 0) - commission;

  const profit = annualRevenue - annualExpenses - monthlyPayment * 12;

  return (
    <div className="bg-brand-alabaster min-h-screen">
      <Navbar />
      <section className="py-16 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-3">The Calculators Suite</div>
        <h1 className="font-display text-5xl md:text-6xl max-w-3xl leading-tight">Run the numbers <span className="italic text-brand-pink">before</span> you run the property.</h1>
        <p className="mt-6 text-neutral-600 max-w-2xl text-lg">Ten calculators used inside real property management deals — ROI, RevPAR, ADR, cash flow, break-even, mortgage, and more.</p>

        <div className="grid lg:grid-cols-2 gap-8 mt-14">
          <Card title="ROI Calculator" icon={TrendingUp} testid="calc-roi">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Purchase Price" value={purchase} onChange={setPurchase} suffix="$" testid="roi-purchase" />
              <Field label="Down Payment" value={down} onChange={setDown} suffix="$" testid="roi-down" />
              <Field label="Nightly Rate" value={nightly} onChange={setNightly} suffix="$" testid="roi-nightly" />
              <Field label="Occupancy" value={occupancy} onChange={setOccupancy} suffix="%" testid="roi-occ" />
              <Field label="Monthly Expenses" value={expenses} onChange={setExpenses} suffix="$" testid="roi-expenses" />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-6">
              <Result label="Annual Revenue" value={currency(annualRevenue)} />
              <Result label="Net Op Income" value={currency(noi)} />
              <Result label="Cash-on-Cash ROI" value={pct(cashOnCash)} big />
            </div>
          </Card>

          <Card title="Cash Flow Calculator" icon={DollarSign} testid="calc-cashflow">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Nightly Rate" value={nightly} onChange={setNightly} suffix="$" testid="cf-nightly" />
              <Field label="Occupancy" value={occupancy} onChange={setOccupancy} suffix="%" testid="cf-occ" />
              <Field label="Monthly Expenses" value={expenses} onChange={setExpenses} suffix="$" testid="cf-expenses" />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-6">
              <Result label="Monthly Cash Flow" value={currency(cashFlowMonthly)} big />
            </div>
          </Card>

          <Card title="ADR + RevPAR" icon={Calculator} testid="calc-adr">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Total Revenue" value={revenue} onChange={setRevenue} suffix="$" testid="adr-rev" />
              <Field label="Nights Booked" value={nightsBooked} onChange={setNightsBooked} testid="adr-nb" />
              <Field label="Available Nights" value={totalNights} onChange={setTotalNights} testid="adr-total" />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-6">
              <Result label="ADR" value={currency(adr)} />
              <Result label="RevPAR" value={currency(revpar)} />
            </div>
          </Card>

          <Card title="Occupancy Rate" icon={Percent} testid="calc-occ">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Nights Booked" value={nightsBooked} onChange={setNightsBooked} testid="occ-booked" />
              <Field label="Available Nights" value={availableNights} onChange={setAvailableNights} testid="occ-avail" />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-6">
              <Result label="Occupancy Rate" value={pct(occRate)} big />
            </div>
          </Card>

          <Card title="Break-Even Nights" icon={TrendingUp} testid="calc-breakeven">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Monthly Fixed Costs" value={monthlyFixed} onChange={setMonthlyFixed} suffix="$" testid="be-fixed" />
              <Field label="Average ADR" value={avgAdr} onChange={setAvgAdr} suffix="$" testid="be-adr" />
              <Field label="Variable Cost / Night" value={varCostPerNight} onChange={setVarCostPerNight} suffix="$" testid="be-var" />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-6">
              <Result label="Break-Even Nights / mo" value={isFinite(breakEvenNights) ? Math.ceil(breakEvenNights) : "—"} big />
            </div>
          </Card>

          <Card title="Mortgage Calculator" icon={Home} testid="calc-mortgage">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Loan Amount" value={loan} onChange={setLoan} suffix="$" testid="mtg-loan" />
              <Field label="Interest Rate" value={rate} onChange={setRate} suffix="%" testid="mtg-rate" />
              <Field label="Term (years)" value={years} onChange={setYears} testid="mtg-years" />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-6">
              <Result label="Monthly Payment" value={currency(monthlyPayment)} big />
              <Result label="Total Interest" value={currency(monthlyPayment * n - (loan || 0))} />
            </div>
          </Card>

          <Card title="Profit Calculator" icon={DollarSign} testid="calc-profit">
            <p className="text-sm text-neutral-500 -mt-3 mb-4">Uses inputs from ROI + Mortgage above.</p>
            <div className="grid grid-cols-2 gap-3">
              <Result label="Annual Revenue" value={currency(annualRevenue)} />
              <Result label="Annual Expenses" value={currency(annualExpenses)} />
              <Result label="Mortgage / yr" value={currency(monthlyPayment * 12)} />
              <Result label="Annual Profit" value={currency(profit)} big />
            </div>
          </Card>

          <Card title="Cleaning Margin" icon={Wrench} testid="calc-cleaning">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Cleaning Fee (charged)" value={cleaningFee} onChange={setCleaningFee} suffix="$" testid="clean-fee" />
              <Field label="Cleaning Cost (paid)" value={cleaningCost} onChange={setCleaningCost} suffix="$" testid="clean-cost" />
              <Field label="Turnovers / mo" value={turnovers} onChange={setTurnovers} testid="clean-turns" />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-6">
              <Result label="Monthly Margin" value={currency(cleaningMargin)} big />
              <Result label="Margin %" value={pct(cleaningPct)} />
            </div>
          </Card>

          <Card title="Co-Host Commission" icon={Percent} testid="calc-cohost">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Gross Monthly Revenue" value={grossRev} onChange={setGrossRev} suffix="$" testid="ch-rev" />
              <Field label="Commission" value={commissionPct} onChange={setCommissionPct} suffix="%" testid="ch-pct" />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-6">
              <Result label="Your Commission" value={currency(commission)} big />
              <Result label="Owner Net" value={currency(ownerNet)} />
            </div>
          </Card>

          <Card title="Expense Ratio" icon={Calculator} testid="calc-expense-ratio">
            <p className="text-sm text-neutral-500 -mt-3 mb-4">How much of your revenue disappears each year.</p>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <Result label="Expense Ratio" value={pct(annualRevenue ? (annualExpenses / annualRevenue) * 100 : 0)} big />
              <Result label="Kept per $1" value={annualRevenue ? `$${(1 - annualExpenses / annualRevenue).toFixed(2)}` : "—"} />
            </div>
          </Card>
        </div>
      </section>
      <Footer />
    </div>
  );
}
